export type AudioTrackOption = {
  code: string | null;
  language: string | null;
  kind: 'original' | 'auto_dub' | 'other';
  transcribable: boolean;
};

export type AudioSelection = {
  language: string;
  audio_language: string;
  audio_track: string;
};

/** yt-dlp rarely marks a plain single-track video as "original" (the worker reports
 *  `other`). A lone `other` track IS the original: treating it as such keeps
 *  `audio_track: 'original'` (a raw code would flag ordinary videos as
 *  community-excluded). A lone auto-dub says nothing about the original's
 *  language, so it is dropped and the form asks for the audio language, as when
 *  the lookup finds no usable track. With 2+ tracks and no original, nothing is
 *  inferred. */
export function normalizeTracks(tracks: readonly AudioTrackOption[]): AudioTrackOption[] {
  if (tracks.length === 1 && tracks[0].kind === 'other') {
    return [{ ...tracks[0], kind: 'original' }];
  }
  if (tracks.length === 1 && tracks[0].kind === 'auto_dub') return [];
  return [...tracks];
}

/** Code of the transcribable original track, or null when there is none. */
export function defaultTrackCode(rawTracks: readonly AudioTrackOption[]): string | null {
  const tracks = normalizeTracks(rawTracks);
  const original = tracks.find((t) => t.kind === 'original' && t.transcribable);
  return original?.code ?? null;
}

/** Resolves what the import form submits.
 *
 *  - Lookup `ok` with at least one transcribable track: use the selected track
 *    (default: the original). `audio_track` is `'original'` for the original,
 *    otherwise the raw YouTube code.
 *  - Any other lookup outcome (timeout, error, unsupported, loading, no usable
 *    track): `original` + the manually chosen audio language, so the form
 *    always submits.
 *  - `language` is the subtitle language for PRO users, else the audio language.
 *
 *  Returns null when tracks are usable but nothing is chosen and there is no
 *  default (untranscribable original): the form must keep submit disabled. */
export function buildAudioSelection({
  tracks: rawTracks,
  lookupStatus,
  selectedTrackCode,
  manualAudioLanguage,
  subtitleLanguage,
  hasPro,
}: {
  tracks: readonly AudioTrackOption[];
  lookupStatus: 'ok' | 'timeout' | 'error' | 'unsupported' | 'loading' | string;
  selectedTrackCode: string | null;
  manualAudioLanguage: string;
  subtitleLanguage: string;
  hasPro: boolean;
}): AudioSelection | null {
  const tracks = normalizeTracks(rawTracks);
  const usable = tracks.filter((t) => t.transcribable && t.code && t.language);
  let audioLanguage = manualAudioLanguage;
  let audioTrack = 'original';

  if (lookupStatus === 'ok' && usable.length > 0) {
    const code = usable.some((t) => t.code === selectedTrackCode)
      ? selectedTrackCode
      : defaultTrackCode(tracks);
    const picked = usable.find((t) => t.code === code);
    if (!picked || !picked.language || !picked.code) return null;
    audioLanguage = picked.language;
    audioTrack = picked.kind === 'original' ? 'original' : picked.code;
  }

  return {
    language: hasPro && subtitleLanguage ? subtitleLanguage : audioLanguage,
    audio_language: audioLanguage,
    audio_track: audioTrack,
  };
}
